//import * as Sentry from "@sentry/nextjs";
import domSerializer from "dom-serializer";
import { parseDocument } from "htmlparser2";

import constants from "../constants";
import { trackEvent, identifyUser } from "../utils/analytics";
import { blobServiceClient } from "./azureBlobClient";
// mixpanel initialization moved to utils/analytics.js

const DEFAULT_KELA_API = "https://kela.api.dm2buy.com";
const DEFAULT_LEGACY_API = "https://api.dm2buy.com";
const DEFAULT_LOCAL_API = "http://localhost:8080";

/**
 * A host whose first label is the store subdomain: `<store>.dm2buy.com` in production, or
 * `<store>.<NEXT_PUBLIC_STORE_HOST_SUFFIX>` on a staging box (e.g. `memehub.staging.example`).
 * Any other host is treated as a custom domain and resolved through the backend.
 */
function isStoreHost(host) {
  if (!host) return false;
  const bare = host.split(":")[0];
  if (bare.split(".").includes("dm2buy")) return true;
  const suffix = (process.env.NEXT_PUBLIC_STORE_HOST_SUFFIX || "").replace(/^\./, "");
  return Boolean(suffix) && bare.endsWith(`.${suffix}`);
}

let cachedSubdomain = null;

const getSubDomainOfPage = () => {
  const { host } = window.location;

  let splitHost = host.split(".");
  console.log("===========  TRIGGERED ===========");
  if (!isStoreHost(host) && !splitHost.includes("localhost:3000")) {
    return new Promise((resolve, reject) => {
      fetch(`/api/store/getSubdomain/${encodeURI(window.location.host)}`)
        .then((response) => response.json())
        .then((res) => {
          splitHost[0] = res.subdomain;
          console.log("splitHost[0] ==========: ", splitHost[0]);
          resolve(
            splitHost[0] == "localhost:3000" || splitHost[0] == "192" ? "testing141" : splitHost[0],
          );
        })
        .catch((err) => reject());
    });
  } else {
    return new Promise((resolve, reject) => {
      resolve(
        splitHost[0] == "localhost:3000" || splitHost[0] == "192" ? "testing141" : splitHost[0],
      );
    });
  }
};

const sanitizeHtml = (htmlContent) => {
  try {
    if (!htmlContent) return "";
    const document = parseDocument(htmlContent);
    const fixedHTML = domSerializer(document);
    return fixedHTML;
  } catch (error) {
    console.error("Error fixing HTML:", error);
    return ""; // Return empty string instead of undefined Text
  }
};

const airtableBaseId = process.env.AIRTABLE_BASE_ID;

const cashfreeEnv = process.env.CASHFREE_ENV;

const environment = process.env.ENV;
const nodeEnv = process.env.NEXT_PUBLIC_NODE_ENV;

function parseActiveStores() {
  const raw = process.env.NEXT_PUBLIC_ACTIVE_STORES || "";
  return raw
    .split(",")
    .map((store) => store.trim().toLowerCase())
    .filter(Boolean);
}

function routeAllTrafficToKela() {
  return process.env.NEXT_PUBLIC_ROUTE_ALL_TRAFFIC_TO_KELA === "true";
}

function isLocalHost(host) {
  if (!host) return false;
  return (
    host.startsWith("localhost") ||
    host.startsWith("127.0.0.1") ||
    /^192\.168\.\d{1,3}\.\d{1,3}(:\d+)?$/.test(host)
  );
}

function resolveSubdomainFromHost(host) {
  if (!host) return null;
  if (isLocalHost(host)) {
    return constants.devEnv?.storeSubdomain || null;
  }

  const splitHost = host.split(".");
  if (isStoreHost(host)) {
    return splitHost[0];
  }

  return null;
}

function setResolvedSubdomain(subdomain) {
  if (subdomain) {
    cachedSubdomain = subdomain;
  }
}

function getEndpointOptions(options = {}) {
  if (typeof options === "string") {
    return { subdomain: options };
  }
  return options;
}

function getEndpointOptionsFromRequest(req, extra = {}) {
  return {
    host: req?.headers?.host,
    subdomain:
      extra.subdomain ||
      req?.query?.subdomain ||
      req?.body?.seller?.name ||
      req?.body?.subdomain ||
      undefined,
    ...extra,
  };
}

function getPublicServerEndpoint(options = {}) {
  const { host, subdomain } = getEndpointOptions(options);

  const localUrl = (process.env.NEXT_PUBLIC_LOCAL_SERVER_ENDPOINT || DEFAULT_LOCAL_API).replace(
    /\/$/,
    "",
  );
  const kelaUrl = (process.env.NEXT_PUBLIC_SERVER_ENDPOINT || DEFAULT_KELA_API).replace(/\/$/, "");
  const legacyUrl = (process.env.NEXT_PUBLIC_LEGACY_SERVER_ENDPOINT || DEFAULT_LEGACY_API).replace(
    /\/$/,
    "",
  );

  const resolvedHost = host || (typeof window !== "undefined" ? window.location.host : null);

  if (isLocalHost(resolvedHost)) {
    return localUrl;
  }

  if (routeAllTrafficToKela()) {
    return kelaUrl;
  }

  const resolvedSubdomain = (
    subdomain ||
    cachedSubdomain ||
    resolveSubdomainFromHost(resolvedHost) ||
    ""
  ).toLowerCase();

  if (resolvedSubdomain && parseActiveStores().includes(resolvedSubdomain)) {
    return kelaUrl;
  }

  return legacyUrl;
}

function getServerEndpoint(options = {}) {
  return `${getPublicServerEndpoint(options)}/v2`;
}

/**
 * Every host `getPublicServerEndpoint` can resolve to, regardless of which
 * one a given request actually lands on. For code that needs to recognize
 * "this URL is one of our API hosts" without replaying the subdomain/routing
 * logic above — e.g. deciding whether to attach trace headers.
 */
function getAllPublicServerEndpoints() {
  return [
    (process.env.NEXT_PUBLIC_LOCAL_SERVER_ENDPOINT || DEFAULT_LOCAL_API).replace(/\/$/, ""),
    (process.env.NEXT_PUBLIC_SERVER_ENDPOINT || DEFAULT_KELA_API).replace(/\/$/, ""),
    (process.env.NEXT_PUBLIC_LEGACY_SERVER_ENDPOINT || DEFAULT_LEGACY_API).replace(/\/$/, ""),
  ];
}

const guid = () => {
  var result, i, j;
  result = "";
  for (j = 0; j < 32; j++) {
    if (j == 8 || j == 12 || j == 16 || j == 20) result = result + "-";
    i = Math.floor(Math.random() * 16)
      .toString(16)
      .toUpperCase();
    result = result + i;
  }
  return result;
};

function isNumeric(str) {
  if (typeof str != "string") return false; // we only process strings!
  return (
    !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    !isNaN(parseFloat(str))
  ); // ...and ensure strings of whitespace fail
}

//Stock check is not actually done through this method now
async function getStockAvailability(cartProducts) {
  if (!cartProducts || !cartProducts.length) return false;
  let isStockAvailable = true;
  return isStockAvailable;
}

const uploadFileToAzure = async (file, containerName) => {
  if (!file || !containerName) {
    throw new Error("File and containerName are required.");
  }

  try {
    const containerClient = blobServiceClient.getContainerClient(containerName);
    const blobName = `${Date.now()}-${file.name}`;

    const blockBlobClient = containerClient.getBlockBlobClient(blobName);

    await blockBlobClient.uploadData(file, {
      blobHTTPHeaders: {
        blobContentType: file.type,
      },
    });

    return blockBlobClient.url;
  } catch (error) {
    console.error("Error uploading file to Azure Blob Storage:", error);
    throw error;
  }
};

function getLuminance(hex) {
  // Remove '#' if present
  hex = hex.replace(/^#/, "");

  // Expand shorthand form (#abc) to full form (#aabbcc)
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }

  // Parse r, g, b values
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;

  // Apply sRGB companding
  const compand = (c) => {
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };

  const R = compand(r);
  const G = compand(g);
  const B = compand(b);

  // Calculate luminance
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

const checkProductOrActiveVariantHasLowStock = ({ product, variantName, lowStockThreshold }) => {
  let isSoldOut = false;
  let hasLowStock = false;

  // if lowStockThreshold is not set, or is 0, or product is not set, return false
  if (!lowStockThreshold || lowStockThreshold === 0 || !product) {
    return false;
  }

  if (variantName) {
    const variant = product.variantOptions.find((v) => v.name === variantName);

    // check if the variant is sold out
    if (variant.availableStock === 0) {
      return false;
    }

    // check if the variant has low stock
    if (variant.availableStock <= lowStockThreshold) {
      return true;
    }

    return;
  }

  // check if all variants are sold out
  if (product.variantOptions && product.variantOptions.length) {
    isSoldOut = product.variantOptions.every((variant) => variant.availableStock === 0);
  }
  if (isSoldOut) return false;

  // check if any variant has low stock
  if (product.variantOptions && product.variantOptions.length) {
    hasLowStock = product.variantOptions.some(
      (variant) => variant.availableStock <= lowStockThreshold,
    );
  }
  if (hasLowStock) return true;

  // check if the product is sold out
  if (product.availableStock === 0) return false;

  // check if product has low stock (if no variants)
  if (product.availableStock <= lowStockThreshold) {
    hasLowStock = true;
  }

  return hasLowStock;
};

export {
  nodeEnv,
  getSubDomainOfPage,
  airtableBaseId,
  getServerEndpoint,
  getPublicServerEndpoint,
  getAllPublicServerEndpoints,
  isStoreHost,
  getEndpointOptionsFromRequest,
  setResolvedSubdomain,
  resolveSubdomainFromHost,
  guid,
  isNumeric,
  environment,
  cashfreeEnv,
  getStockAvailability,
  trackEvent,
  identifyUser,
  sanitizeHtml,
  uploadFileToAzure,
  getLuminance,
  checkProductOrActiveVariantHasLowStock,
};
